import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { DONATE_LABEL, DONATE_URL } from '../../config/donate';

export type AuthAction = 'sign-in' | 'sign-out' | null;

interface AuthControlsProps {
  isAuthenticated: boolean;
  user: Record<string, unknown> | null;
  pendingAction: AuthAction;
  onSignIn: () => void;
  onSignOut: () => void;
  onOpenTroubleshooting?: () => void;
  showTroubleshootingEntry?: boolean;
}

interface UserIdentity {
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  initials: string;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toInitials(displayName: string): string {
  const segments = displayName
    .split(/[\s@._-]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (segments.length === 0) {
    return 'CF';
  }

  return segments.map((segment) => segment[0]!.toUpperCase()).join('');
}

function getUserIdentity(user: Record<string, unknown> | null): UserIdentity {
  const name = user ? asTrimmedString(user.name) : null;
  const email = user ? asTrimmedString(user.email) : null;
  const avatarUrl = user ? asTrimmedString(user.avatarUrl) : null;
  const displayName = name ?? email ?? 'Account';

  return {
    displayName,
    email,
    avatarUrl,
    initials: toInitials(displayName),
  };
}

export function AuthControls({
  isAuthenticated,
  user,
  pendingAction,
  onSignIn,
  onSignOut,
  onOpenTroubleshooting,
  showTroubleshootingEntry = false,
}: AuthControlsProps): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);

  const isBusy = pendingAction !== null;
  const signInLabel = pendingAction === 'sign-in' ? 'Signing in...' : 'Sign in';
  const signOutLabel = pendingAction === 'sign-out' ? 'Signing out...' : 'Sign out';

  const userIdentity = getUserIdentity(user);
  const shouldRenderAvatarImage =
    Boolean(userIdentity.avatarUrl) && !avatarImageError;
  const menuItemClassName =
    'block w-full rounded-md border-0 bg-transparent px-2.5 py-[0.55rem] text-left text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const clickedTrigger = triggerRef.current?.contains(target) ?? false;
      const clickedMenu = menuRef.current?.contains(target) ?? false;

      if (!clickedTrigger && !clickedMenu) {
        setMenuOpen(false);
      }
    };

    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      setMenuOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    setAvatarImageError(false);
  }, [userIdentity.avatarUrl]);

  if (!isAuthenticated) {
    return (
      <div className="relative flex shrink-0 items-center self-start">
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={onSignIn}
          disabled={isBusy}
        >
          {signInLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex shrink-0 items-center self-start">
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-border bg-card p-0 transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Open account menu"
        onClick={() => setMenuOpen((current) => !current)}
      >
        {shouldRenderAvatarImage ? (
          <img
            src={userIdentity.avatarUrl!}
            alt={`${userIdentity.displayName} avatar`}
            className="h-full w-full object-cover"
            onError={() => setAvatarImageError(true)}
          />
        ) : (
          <span
            className="inline-flex h-full w-full items-center justify-center bg-muted text-[0.8125rem] font-bold text-muted-foreground"
            aria-hidden
          >
            {userIdentity.initials}
          </span>
        )}
      </button>

      {menuOpen ? (
        <div
          ref={menuRef}
          className="absolute right-0 top-[calc(100%+0.5rem)] z-80 w-[min(18rem,calc(100vw-1.5rem))] rounded-xl border border-border bg-card shadow-[0_14px_30px_rgba(15,23,42,0.16)] sm:w-[min(20rem,calc(100vw-2rem))]"
          role="menu"
          aria-label="Account menu"
        >
          <div className="flex items-start gap-2.5 border-b border-border p-3.5">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full" aria-hidden>
              {shouldRenderAvatarImage ? (
                <img
                  src={userIdentity.avatarUrl!}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setAvatarImageError(true)}
                />
              ) : (
                <span className="inline-flex h-full w-full items-center justify-center bg-muted text-[0.8125rem] font-bold text-muted-foreground">
                  {userIdentity.initials}
                </span>
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-[0.35rem]">
              <p className="m-0 text-[1.0625rem] font-bold leading-[1.2] text-foreground">
                {userIdentity.displayName}
              </p>
              {userIdentity.email ? (
                <p className="m-0 break-words text-[0.8125rem] leading-[1.25] text-muted-foreground">
                  {userIdentity.email}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col p-[0.35rem]">
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              className={menuItemClassName}
              onClick={() => setMenuOpen(false)}
            >
              {DONATE_LABEL}
            </a>
            {showTroubleshootingEntry && onOpenTroubleshooting ? (
              <button
                type="button"
                role="menuitem"
                className={menuItemClassName}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenTroubleshooting();
                }}
              >
                Troubleshooting
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className={`${menuItemClassName} text-destructive`}
              onClick={() => {
                setMenuOpen(false);
                onSignOut();
              }}
              disabled={isBusy}
            >
              {signOutLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
